import { useWorkspaceText } from '../../workspace/interface-copy';
import { AgentProposal } from '@/workspace/agents/proposal';
import { useEffect, useState } from 'react';
import { useNamespaceCopy } from '@/i18n/ns';
import artifactPanelsCopy from '@/i18n/ns/artifact-panels';
import { CheckIcon } from 'lucide-react';
import { CanvasFooter } from '@/workspace/canvas-footer';
import { ActionButton } from './action-button';
import { useActionSummary, statusLabel } from './action-approval-copy';
import { actionPrimaryLabel } from './action-labels';
import { ActionExpiry, useActionExpiry } from './action-expiry';
import { EditableFields } from './action-fields';
import { documentDiffCopy } from '@/workspace/document-diff-copy';
import { ActionDraftDiff } from './action-draft-diff';
import { ActionDraft } from './action-draft';
import { ActionDetails } from './action-details';
import { actionUsesCanvas } from './action-layout';
import { ActionPager } from './action-pager';
import { MeetingNoteDestination } from './meeting-note-destination';
import { PageSuggestionChanges, usePageSuggestion } from './page-suggestion';
import { StorefrontActionDraft, storefrontChangeKind } from '@/workspace/storefront/action-draft';
import { storeRead } from '@/workspace/storefront/read-request';
import storefrontCopy from '@/i18n/ns/storefront';
import type { ActionView } from './action-confirmation';
import type { useActionConfirmation } from './use-action-confirmation';

type State = ReturnType<typeof useActionConfirmation>;

/**
 * Only the object currently displayed owns a canvas approval footer.
 * `restagedWithEdits` is true only for the exact request the server returned
 * from this canvas's own successful "Save changes"; it grants and records nothing.
 */
export function ActionCanvasReview({
  state,
  action,
  restagedWithEdits = false,
}: {
  state: State;
  action: ActionView;
  restagedWithEdits?: boolean;
}) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const {
    workspace,
    actionText,
    surfaceCopy,
    locale,
    copy,
    edit,
    draft,
    setDraft,
    busy,
    canApprove,
    uncertain,
    notice,
    mutate,
    checkAgain,
  } = state;
  const expiry = useActionExpiry(action.expiresAt);
  // A knowledge page suggestion names its page in words and opens on its Changes.
  const suggestion = usePageSuggestion(action);
  // null until the person picks a tab: each request opens on its own first tab.
  const [localTab, setLocalTab] = useState<string | null>(null);
  const [diffStats, setDiffStats] = useState<{ addedWords: number; removedWords: number } | null>(
    null,
  );
  const diffCopy = documentDiffCopy(locale);
  const hasChanges =
    action.tool === 'update_skill_draft' ||
    action.tool === 'propose_knowledge_change' ||
    Boolean(suggestion?.hasChanges) ||
    (action.tool === 'save_work_result' && typeof action.args.replaces === 'string');
  const pending =
    !expiry.expired &&
    action.status === 'pending' &&
    (action.canDecide || edit) &&
    actionUsesCanvas(action);
  const isAgent = ['create_agent', 'update_agent', 'set_agent_trigger'].includes(action.tool);
  // A shopping assistant change shows what it makes (the widget, the notes); it is changed by
  // asking in the chat, never field by field.
  const storefront = storefrontChangeKind(action.tool);
  const storefrontText = useNamespaceCopy(storefrontCopy);
  // A paid read of the shop starts on approval: its button and footer say so.
  const storefrontRead = storefront === 'read';
  const title =
    action.tool === 'add_meeting_note'
      ? actionText.types.add_meeting_note
      : suggestion
        ? suggestion.heading
        : (action.args.name ?? action.args.title);
  const summary = useActionSummary()(action);
  // The native build is presented by its kind and what it does; the exact request it will run
  // is read once, under Content in the Draft tab.
  const panels = useNamespaceCopy(artifactPanelsCopy);
  // Why the primary action cannot be pressed yet (no live card token): first "getting ready", and
  // only if that lasts, the plain not-ready line. A known specific reason (expired, not yours to
  // decide) never reaches this footer, so nothing more specific is hidden by these.
  const waitingForToken = pending && !canApprove && !busy && !edit && !uncertain;
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    setSlow(false);
    if (!waitingForToken) return;
    const timer = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(timer);
  }, [waitingForToken]);
  const buildRequest =
    action.tool === 'build_artifact' && typeof action.args.task === 'string'
      ? action.args.task
      : null;
  const firstTab = isAgent
    ? action.tool === 'set_agent_trigger'
      ? 'schedule'
      : 'setup'
    : action.tool === 'save_work_result'
      ? 'preview'
      : suggestion?.hasChanges
        ? 'changes'
        : 'draft';
  const tabs =
    firstTab === 'changes'
      ? ['changes', 'draft', 'details']
      : [
          firstTab,
          ...(hasChanges ? ['changes'] : []),
          ...(action.tool === 'create_agent' ? ['schedule'] : []),
          'details',
        ];
  const requestedTab = workspace?.canvas?.tab ?? localTab ?? firstTab;
  const tab = tabs.includes(requestedTab) ? requestedTab : firstTab;
  const selectTab = (next: string) => {
    setLocalTab(next);
    if (workspace?.canvas?.kind === 'action')
      workspace.openCanvas({ ...workspace.canvas, tab: next });
  };
  // Approving a page suggestion sends it for review; a publisher still accepts it.
  const primary =
    suggestion?.send ??
    (storefrontRead
      ? // Checking saved work again is not a read: its button says what Setup's button says.
        storeRead(action.tool, action.args) === 'checks'
        ? storefrontText.checkAgain
        : storefrontText.readStart
      : actionPrimaryLabel(
          action,
          surfaceCopy,
          action.tool === 'add_meeting_note'
            ? actionText.types.add_meeting_note
            : actionText.approve,
          locale,
        ));
  const shownAction = ['create_skill_draft', 'update_skill_draft'].includes(action.tool)
    ? {
        ...action,
        args: Object.fromEntries(
          Object.entries(action.args).filter(([key]) =>
            ['name', 'description', 'instructions'].includes(key),
          ),
        ),
      }
    : action.tool === 'create_agent'
      ? {
          ...action,
          args:
            tab === 'schedule'
              ? { schedule: action.args.schedule ?? '—' }
              : Object.fromEntries(
                  Object.entries(action.args).filter(([key]) => key !== 'schedule'),
                ),
        }
      : suggestion
        ? { ...action, args: suggestion.draftArgs }
        : action;
  return (
    <section
      className="pc-action-confirmation pc-action-confirmation-canvas"
      data-status={action.status}
      aria-label={summary}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && edit) {
          event.stopPropagation();
          event.preventDefault();
          if (!busy) void mutate('restage', action.args);
        }
      }}
    >
      <ActionPager id={action.id} />
      <header>
        {buildRequest === null && (
          <span className="pc-action-confirmation-kicker">
            {storefront
              ? storefrontText.assistantTitle
              : (actionText.types[action.tool as keyof typeof actionText.types] ??
                actionText.review)}
          </span>
        )}
        <h3>{buildRequest !== null ? primary : typeof title === 'string' ? title : summary}</h3>
        <div className="pc-action-canvas-meta">
          {restagedWithEdits && action.status === 'pending' && !edit && (
            <>
              <span className="pc-action-updated">
                <CheckIcon aria-hidden="true" />
                {copy.actionUpdatedWithEdits}
              </span>
              {' · '}
            </>
          )}
          {buildRequest !== null && <>{panels.kindDocument} · </>}
          <span role="status">
            {expiry.expired && action.status === 'pending'
              ? actionText.expired
              : statusLabel(action.status, actionText)}
          </span>
          {/* A decided read has nothing left to expire. */}
          {((buildRequest === null && !storefrontRead) ||
            (action.status === 'pending' && !expiry.expired)) && (
            <>
              {' '}
              ·{' '}
              <ActionExpiry expiresAt={action.expiresAt} copy={surfaceCopy} locale={locale} near />
            </>
          )}
        </div>
      </header>
      <div className="ws-tabs pc-action-canvas-tabs" role="tablist" aria-label={summary}>
        {tabs.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            tabIndex={tab === key ? 0 : -1}
            onKeyDown={(event) => {
              if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const direction = document.dir === 'rtl' ? -1 : 1;
              const index = tabs.indexOf(tab);
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? tabs.length - 1
                    : (index +
                        (event.key === 'ArrowRight' ? direction : -direction) +
                        tabs.length) %
                      tabs.length;
              selectTab(tabs[next]);
              event.currentTarget.parentElement
                ?.querySelectorAll<HTMLButtonElement>('button')
                [next]?.focus();
            }}
            className={tab === key ? 'active' : ''}
            onClick={() => selectTab(key)}
          >
            {key === 'changes'
              ? diffCopy.changes
              : isAgent && key === 'setup'
                ? uiText('Agent')
                : isAgent && key === 'schedule'
                  ? uiText('Triggers')
                  : storefrontRead && key === 'draft'
                    ? storefrontText.readTab
                    : surfaceCopy.tabs[key as keyof typeof surfaceCopy.tabs]}
            {key === 'changes' && diffStats && (
              <span className="ws-diff-tab-stats">
                {' '}
                +{diffStats.addedWords} −{diffStats.removedWords}
              </span>
            )}
          </button>
        ))}
      </div>
      <div className="pc-action-canvas-content" role="tabpanel">
        {action.tool === 'add_meeting_note' && typeof action.args.meetingId === 'string' && (
          <MeetingNoteDestination meetingId={action.args.meetingId} />
        )}
        {hasChanges && !edit && (
          <div hidden={tab !== 'changes'}>
            {suggestion ? (
              <PageSuggestionChanges
                view={suggestion}
                action={action}
                copy={actionText}
                onStats={setDiffStats}
              />
            ) : (
              <ActionDraftDiff action={action} copy={actionText} onStats={setDiffStats} />
            )}
          </div>
        )}
        {tab === 'changes' && !edit ? null : tab === 'details' ? (
          <ActionDetails action={action} copy={actionText} />
        ) : isAgent ? (
          <AgentProposal
            args={edit ? draft : action.args}
            tab={tab}
            editing={Boolean(edit && pending)}
            onChange={setDraft}
            triggerOnly={action.tool === 'set_agent_trigger'}
          />
        ) : storefront ? (
          <StorefrontActionDraft
            tool={action.tool}
            args={action.args}
            status={action.status}
            result={action.result}
          />
        ) : edit && pending ? (
          <EditableFields args={draft} onChange={setDraft} copy={actionText} />
        ) : (
          <ActionDraft action={shownAction} copy={actionText} />
        )}
        {action.approval?.method === 'message' && (
          <p className="pc-action-confirmation-receipt">
            {actionText.approvedByMessage}: “{action.approval.text}”
          </p>
        )}
        {(action.error || notice) && (
          <p className="pc-action-confirmation-error" role="alert">
            {action.error || notice}
          </p>
        )}
        {!action.canDecide && !edit && action.status === 'pending' && (
          <p role="status">{surfaceCopy.waitingFor(action.accountLabel ?? copy.account)}</p>
        )}
      </div>
      {pending && (
        <CanvasFooter className="pc-action-canvas-footer">
          <span className="pc-action-confirmation-hint">
            {uncertain
              ? surfaceCopy.uncertain
              : edit
                ? surfaceCopy.editingFooter
                : isAgent
                  ? uiText(
                      'Review the agent and its trigger. Approval applies exactly what is shown.',
                    )
                  : (suggestion?.footer ??
                    (storefrontRead ? storefrontText.readFooter : surfaceCopy.pendingFooter))}
          </span>
          <span className="pc-action-ready-note" role="status">
            {waitingForToken ? (slow ? panels.approveNotReady : panels.approveGettingReady) : ''}
          </span>
          <div className="pc-action-confirmation-actions">
            {uncertain ? (
              <ActionButton
                variant="secondary"
                busy={busy}
                disabled={busy}
                onClick={() => void checkAgain()}
              >
                {surfaceCopy.checkAgain}
              </ActionButton>
            ) : edit ? (
              <>
                <ActionButton
                  variant="quiet"
                  disabled={busy}
                  onClick={() => void mutate('restage', action.args)}
                >
                  {copy.cancel}
                </ActionButton>
                <ActionButton
                  variant="primary"
                  busy={busy}
                  disabled={busy}
                  onClick={() => void mutate('restage', draft)}
                >
                  {surfaceCopy.saveChanges}
                </ActionButton>
              </>
            ) : (
              <>
                <ActionButton variant="quiet" disabled={busy} onClick={() => void mutate('reject')}>
                  {actionText.reject}
                </ActionButton>
                {/* A page suggestion's edits are exact text pairs, not fields a person
                    edits; asking in the chat redrafts it. */}
                {!suggestion && !storefront && (
                  <ActionButton
                    variant="secondary"
                    disabled={busy}
                    onClick={() => void mutate('editing')}
                  >
                    {copy.edit}
                  </ActionButton>
                )}
                <ActionButton
                  variant="primary"
                  busy={busy}
                  disabled={busy || !canApprove}
                  aria-label={`${actionText.approve}: ${primary} — ${typeof title === 'string' ? title : summary}`}
                  onClick={(event) => void mutate('approve', undefined, event)}
                >
                  {primary}
                </ActionButton>
              </>
            )}
          </div>
        </CanvasFooter>
      )}
    </section>
  );
}
